import { useCallback, useEffect, useState } from 'react';

/**
 * Hook to detect if a flex container with flex-wrap is currently wrapping its children
 * @returns A callback ref to attach to the flex container and a boolean indicating if wrapping is occurring
 */
export function useFlexWrap() {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [isWrapping, setIsWrapping] = useState(false);

    const containerRef = useCallback((node: HTMLDivElement | null) => {
        setContainer(node);
    }, []);

    useEffect(() => {
        if (!container) {
            setIsWrapping(false);
            return;
        }

        const checkWrap = () => {
            // Get all direct children
            const children = Array.from(container.children) as HTMLElement[];
            if (children.length === 0) {
                setIsWrapping(false);
                return;
            }
            // Calculate the total width of all children (including gaps)
            const containerRect = container.getBoundingClientRect();
            const containerWidth = containerRect.width;

            // Get computed styles to account for gap
            const computedStyle = window.getComputedStyle(container);
            const gap = parseFloat(computedStyle.gap) || 0;
            const rowGap = parseFloat(computedStyle.rowGap) || gap;

            // Calculate if items would fit on one line
            let totalWidth = 0;
            let maxChildHeight = 0;

            children.forEach((child) => {
                const childRect = child.getBoundingClientRect();
                totalWidth += childRect.width;
                if (children.indexOf(child) < children.length - 1) {
                    totalWidth += gap; // Add gap between items
                }
                maxChildHeight = Math.max(maxChildHeight, childRect.height);
            });

            // Check if wrapping is occurring by comparing container height
            // If wrapped, the container height will be greater than the max child height
            const containerHeight = containerRect.height;
            const expectedSingleLineHeight = maxChildHeight;

            // Account for row gap if there are multiple rows
            const hasMultipleRows = containerHeight > expectedSingleLineHeight + rowGap / 2;

            // Also check if total width exceeds container width (more reliable)
            const wouldWrap = totalWidth > containerWidth;

            setIsWrapping(hasMultipleRows || wouldWrap);
        };

        // Initial check
        checkWrap();

        // Use ResizeObserver to watch for size changes
        const resizeObserver = new ResizeObserver(() => {
            // Use requestAnimationFrame to ensure layout has settled
            requestAnimationFrame(checkWrap);
        });

        resizeObserver.observe(container);

        // Also observe children in case they change size
        const children = Array.from(container.children);
        children.forEach((child) => {
            resizeObserver.observe(child);
        });

        return () => {
            resizeObserver.disconnect();
        };
    }, [container]);

    return { containerRef, isWrapping };
}